import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "../Modal";
import api from "../../utils/api";
import Menu, { MenuItem } from "../Menu";
import Slider from "../Slider";
import AddUserModal from "./AddUserModal";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../contexts/UserContext";

type UserSummary = {
  userId: string;
  displayUserId: string;
  name: string;
  email: string;
  role?: string;
};

type AddLotModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (lotId?: string) => void;
};

const canadianProvinces = [
  { value: "AB", label: "Alberta" },
  { value: "BC", label: "British Columbia" },
  { value: "MB", label: "Manitoba" },
  { value: "NB", label: "New Brunswick" },
  { value: "NL", label: "Newfoundland and Labrador" },
  { value: "NS", label: "Nova Scotia" },
  { value: "NT", label: "Northwest Territories" },
  { value: "NU", label: "Nunavut" },
  { value: "ON", label: "Ontario" },
  { value: "PE", label: "Prince Edward Island" },
  { value: "QC", label: "Quebec" },
  { value: "SK", label: "Saskatchewan" },
  { value: "YT", label: "Yukon" },
];

const usStates = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "DC", label: "District of Columbia" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

const AddLotModal: React.FC<AddLotModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const { fetchUserLots } = useUser();
  const [companyName, setCompanyName] = useState("");
  const [lotName, setLotName] = useState("New Parking Lot");
  const [capacity, setCapacity] = useState<string>("");
  const [country, setCountry] = useState("Canada");
  const [streetNumber, setStreetNumber] = useState("");
  const [streetName, setStreetName] = useState("");
  const [city, setCity] = useState("");
  const [region, setRegion] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [isOwnerMenuOpen, setIsOwnerMenuOpen] = useState(false);
  const [ownerSearchTerm, setOwnerSearchTerm] = useState("");
  const [isCountryMenuOpen, setIsCountryMenuOpen] = useState(false);
  const [isRegionMenuOpen, setIsRegionMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSeeding, setIsSeeding] = useState(false);
  const [isDemoLot, setIsDemoLot] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      resetForm();
    }
  }, [isOpen]);

  const resetForm = () => {
    setCompanyName("");
    setLotName("New Parking Lot");
    setCapacity("");
    setCountry("Canada");
    setStreetNumber("");
    setStreetName("");
    setCity("");
    setRegion("");
    setPostalCode("");
    setOwnerId("");
    setIsDemoLot(false);
    setError(null);
    setFieldErrors({});
  };

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get("/users/get-all-users");
      const payload = response.data;
      if (payload?.success && Array.isArray(payload.data)) {
        setUsers(payload.data);
      }
    } catch (fetchError) {
      console.error("Failed to load users", fetchError);
    }
  }, []);

  useEffect(() => {
    if (!isOwnerMenuOpen) {
      setOwnerSearchTerm("");
    }
  }, [isOwnerMenuOpen]);

  const filteredUsers = useMemo(() => {
    const allowedUsers = users.filter((user: any) => {
      const role = (user.role || "").toLowerCase();
      return role === "operator" || role === "superadmin";
    });
    if (!ownerSearchTerm) return allowedUsers;
    const term = ownerSearchTerm.toLowerCase();
    return allowedUsers.filter((user: any) => {
      const id = user.displayUserId?.replace("PWP-U-", "") || user.displayUserId || "";
      return (
        user.email?.toLowerCase().includes(term) ||
        user.name?.toLowerCase().includes(term) ||
        id.toLowerCase().includes(term)
      );
    });
  }, [users, ownerSearchTerm]);

  const ownerMenuItems = useMemo<MenuItem[]>(() => {
    return filteredUsers.map((user) => {
      const formatted = user.displayUserId?.replace("PWP-U-", "") || user.displayUserId;
      const isSuperadminRole = (user.role || "").toLowerCase() === "superadmin";
      return {
        key: user.userId,
        label: (
          <div className="owner-menu-item">
            <div className="owner-menu-email">
              {user.email}
              {isSuperadminRole && <img src="/assets/superadmin/superadmin.svg" alt="Superadmin" className="owner-menu-icon" />}
            </div>
            <div className="owner-menu-id">{formatted}</div>
          </div>
        ),
        selected: user.userId === ownerId,
        onSelect: () => setOwnerId(user.userId),
      };
    });
  }, [filteredUsers, ownerId]);

  const countryOptions: MenuItem[] = [
    {
      key: "canada",
      label: "Canada",
      selected: country === "Canada",
      onSelect: () => {
        setCountry("Canada");
        setRegion("");
        setPostalCode("");
      },
    },
    {
      key: "united-states",
      label: "United States",
      selected: country === "United States",
      onSelect: () => {
        setCountry("United States");
        setRegion("");
        setPostalCode("");
      },
    },
  ];

  const regionOptions: MenuItem[] = (country === "Canada" ? canadianProvinces : usStates).map((item) => ({
    key: item.value,
    label: item.label,
    selected: region === item.value,
    onSelect: () => setRegion(item.value),
  }));

  const formattedAddress = `${streetNumber.trim()} ${streetName.trim()}, ${city.trim()}, ${region}, ${postalCode.trim()}, ${country}`
    .replace(/\s+,/g, ",")
    .trim();

  const hasValidationErrors = () => {
    const newErrors: Record<string, string | null> = {};

    const maxLengthCheck = (value: string, max: number, label: string) => {
      if (!value.trim()) return `${label} is required.`;
      if (value.trim().length > max) return `${label} must be ${max} characters or less.`;
      return null;
    };

    const trimmedCompany = companyName.trim();
    const trimmedLot = lotName.trim();
    const trimmedStreetNumber = streetNumber.trim();
    const trimmedStreetName = streetName.trim();
    const trimmedCity = city.trim();

    newErrors.companyName = maxLengthCheck(trimmedCompany, 80, "Company name");
    newErrors.lotName = maxLengthCheck(trimmedLot, 60, "Lot name");
    newErrors.streetNumber = trimmedStreetNumber ? null : "Street number is required.";
    newErrors.streetName = maxLengthCheck(trimmedStreetName, 60, "Street name");
    newErrors.city = maxLengthCheck(trimmedCity, 40, "City");
    newErrors.region = region ? null : `${country === "Canada" ? "Province" : "State"} is required.`;

    if (!postalCode.trim()) {
      newErrors.postalCode = `${country === "Canada" ? "Postal" : "Zip"} code is required.`;
    } else if (
      country === "Canada" &&
      !/^[A-Za-z]\d[A-Za-z][ ]?\d[A-Za-z]\d$/.test(postalCode.replace(/\s/g, ""))
    ) {
      newErrors.postalCode = "Postal code must be in A1A 1A1 format.";
    } else if (country !== "Canada" && !/^\d{5}(-\d{4})?$/.test(postalCode)) {
      newErrors.postalCode = "Zip code must be 5 or 9 digits.";
    } else {
      newErrors.postalCode = null;
    }

    if (capacity !== "" && (Number.isNaN(Number(capacity)) || Number(capacity) < 0)) {
      newErrors.capacity = "Capacity must be a positive number.";
    } else {
      newErrors.capacity = null;
    }

    newErrors.owner = ownerId ? null : "Please select an owner.";

    setFieldErrors(newErrors);
    return Object.values(newErrors).some((message) => message);
  };

  const handleCreateLot = async () => {
    if (hasValidationErrors()) {
      setError("Please complete all required fields and fix validation issues.");
      return;
    }

    const trimmedCompany = companyName.trim();
    const trimmedLot = lotName.trim();

    setIsLoading(true);
    setError(null);
    try {
      const createResponse = await api.post("/parkinglots/create-parking-lot", {
        ownerId,
        companyName: trimmedCompany,
        address: formattedAddress,
        lotName: trimmedLot,
        lotCapacity: capacity ? Number(capacity) : 0,
        hasEvCharging: false,
        isDevelopmentLot: isDemoLot,
        createdBy: "SUPERADMIN_USER_ID",
      });

      const newLotId =
        createResponse?.data?.data?.lotId ||
        createResponse?.data?.lotId;

      if (isDemoLot) {
        if (newLotId) {
          setIsSeeding(true);
          try {
            await api.post(`/parkinglots/seed-demo-data/${newLotId}`);
          } catch (seedError: any) {
            const seedMessage =
              seedError?.response?.data?.message ||
              seedError?.message ||
              "Lot was created but demo seeding failed.";
            setError(seedMessage);
            setIsSeeding(false);
            return;
          }
          setIsSeeding(false);
        }
      }

      onSuccess?.(newLotId);
      await fetchUserLots();
      onClose();
      if (newLotId) {
        await new Promise((resolve) => window.setTimeout(resolve, 1000));
        navigate(`/lot/${newLotId}/dashboard/revenue`);
      }
    } catch (submissionError: any) {
      const message =
        submissionError?.response?.data?.message ||
        submissionError?.message ||
        "Failed to create parking lot.";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onCancel={onClose}
        title="Add New Parking Lot"
        hideButtons
      >
        <div className="modal-form">
          <div className="modal-field-group">
            <label>
              Company Name *
              <input value={companyName} onChange={(e) => setCompanyName(e.target.value)} maxLength={80} />
            </label>
            {fieldErrors.companyName && <div className="modal-error">{fieldErrors.companyName}</div>}
          </div>

          <div className="modal-field-group">
            <label>
              Lot Name *
              <input value={lotName} onChange={(e) => setLotName(e.target.value)} maxLength={60} />
            </label>
            {fieldErrors.lotName && <div className="modal-error">{fieldErrors.lotName}</div>}
          </div>

          <div className="modal-form-row">
            <div className="modal-field-group">
              <label>
                Capacity
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  min={0}
                  value={capacity}
                  onChange={(e) => {
                    const numeric = e.target.value.replace(/[^0-9]/g, "");
                    setCapacity(numeric);
                  }}
                />
              </label>
              {fieldErrors.capacity && <div className="modal-error">{fieldErrors.capacity}</div>}
            </div>
            <div className="modal-field-group modal-demo-toggle-group">
              <div className="modal-demo-toggle-row">
                <Slider checked={isDemoLot} onChange={() => setIsDemoLot((v) => !v)} />
                <span>Demo Lot</span>
              </div>
            </div>
          </div>

          <div className="modal-form-row">
            <div className="modal-field-group">
              <label>
                Street Number *
                <input
                  value={streetNumber}
                  onChange={(e) => setStreetNumber(e.target.value.replace(/[^0-9]/g, ""))}
                  maxLength={10}
                />
              </label>
              {fieldErrors.streetNumber && <div className="modal-error">{fieldErrors.streetNumber}</div>}
            </div>
            <div className="modal-field-group">
              <label>
                Street Name *
                <input value={streetName} onChange={(e) => setStreetName(e.target.value)} maxLength={60} />
              </label>
              {fieldErrors.streetName && <div className="modal-error">{fieldErrors.streetName}</div>}
            </div>
            <div className="modal-field-group">
              <label>
                City *
                <input value={city} onChange={(e) => setCity(e.target.value)} maxLength={40} />
              </label>
              {fieldErrors.city && <div className="modal-error">{fieldErrors.city}</div>}
            </div>
          </div>

          <div className="modal-form-row">
            <div className="modal-field-group">
              <label>Country *</label>
              <Menu
                items={countryOptions}
                onOpenChange={setIsCountryMenuOpen}
                closeOnSelect
                trigger={
                  <button type="button" className="menu-trigger modal-menu-trigger">
                    {country}
                  </button>
                }
                dropdownClassName="modal-menu-dropdown"
              />
            </div>
            <div className="modal-field-group">
              <label>{country === "Canada" ? "Province" : "State"} *</label>
              <Menu
                items={regionOptions}
                onOpenChange={setIsRegionMenuOpen}
                closeOnSelect
                trigger={
                  <button type="button" className="menu-trigger modal-menu-trigger">
                    {region ? region : `Select ${country === "Canada" ? "Province" : "State"}`}
                  </button>
                }
                dropdownClassName="modal-menu-dropdown"
              />
              {fieldErrors.region && <div className="modal-error">{fieldErrors.region}</div>}
            </div>
            <div className="modal-field-group">
              <label>
                {country === "Canada" ? "Postal Code" : "Zip Code"} *
                <input
                  value={postalCode}
                  maxLength={country === "Canada" ? 7 : 10}
                onChange={(e) => setPostalCode(e.target.value.toUpperCase())}
              />
            </label>
              {fieldErrors.postalCode && <div className="modal-error">{fieldErrors.postalCode}</div>}
            </div>
          </div>

          <div className="modal-form-row modal-row-owner">
            <div className="modal-owner-select">
              <label>Owner *</label>
              <Menu
                items={ownerMenuItems}
                onOpenChange={setIsOwnerMenuOpen}
                closeOnSelect
                customContent={
                  <div className="menu-search-container">
                    <input
                      type="text"
                      placeholder="Search by email, ID, or name..."
                      value={ownerSearchTerm}
                      onChange={(e) => setOwnerSearchTerm(e.target.value)}
                    />
                  </div>
                }
                trigger={
                  <button type="button" className="menu-trigger modal-menu-trigger">
                    {ownerId
                      ? (() => {
                          const found = users.find((u) => u.userId === ownerId);
                          if (!found) return "Select an owner...";
                          const displayId = found.displayUserId?.replace("PWP-U-", "") || found.displayUserId;
                          return `${displayId} • ${found.email}`;
                        })()
                      : "Select an owner..."}
                  </button>
                }
                dropdownClassName="modal-menu-dropdown"
              />
              {fieldErrors.owner && <div className="modal-error">{fieldErrors.owner}</div>}
            </div>
            <button type="button" className="button-secondary" onClick={() => setShowCreateUser(true)}>
              New User
            </button>
          </div>

          {error && <div className="modal-error">{error}</div>}

          <div className="modal-actions-inline">
            <button className="button-secondary" onClick={onClose} disabled={isLoading}>
              Cancel
            </button>
            <button className="button-primary" onClick={handleCreateLot} disabled={isLoading}>
              {isSeeding ? "Seeding demo data..." : isLoading ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </Modal>

      <AddUserModal
        isOpen={showCreateUser}
        onClose={() => setShowCreateUser(false)}
        title="Create Owner"
        onCreated={(id) => {
          setOwnerId(id);
          fetchUsers();
          setShowCreateUser(false);
        }}
      />
    </>
  );
};

export default AddLotModal;
